import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EvidenceScope,
  EvidenceStatus,
  FileAttachmentIntake,
  ProvidedEvidenceIntake,
  RequirementIntake,
} from 'api';
import { UploadEvidenceDocumentResult } from 'component/details/requirement-intake/requirement-intake-command.service';
import {
  EvidenceIntake,
  EvidenceSlotCardComponent,
} from './evidence-slot-card/evidence-slot-card.component';

const INITIAL_OTHER_EVIDENCE_ID = -1;
const INITIAL_OTHER_EVIDENCE_TYPE = 'OTHER';

const createInitialOtherEvidence = (translateService: TranslateService): EvidenceIntake => ({
  id: INITIAL_OTHER_EVIDENCE_ID,
  name: translateService.instant('requirementIntake.evidence.other.title'),
  status: EvidenceStatus.NotProvided,
  type: INITIAL_OTHER_EVIDENCE_TYPE,
  scope: EvidenceScope.Revision,
  description: translateService.instant('requirementIntake.evidence.other.description'),
  fileAttachments: [],
});

interface UploadedAttachmentViewModel {
  readonly filename: string;
  readonly fileType: string;
}

@Component({
  selector: 'comp-evidence-slots',
  templateUrl: './evidence-slots.component.html',
  styleUrls: ['./evidence-slots.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [EvidenceSlotCardComponent, TranslateModule],
})
export class EvidenceSlotsComponent {
  private readonly translateService = inject(TranslateService);

  readonly requirement = input.required<RequirementIntake>();
  readonly collapsed = input<boolean>(false);
  readonly notifyReloadNeeded = output<number>();

  protected readonly expanded = signal(true);

  //Local state for the component, which is derived from the input requirement.
  //Allows us to update the displayed requirement without mutating the input directly.
  protected readonly displayedRequirement = signal<RequirementIntake | undefined>(undefined);

  // The evidences that are mandatory for the requirement.
  // There is also an option to upload "Other" evidence.
  protected readonly mandatoryEvidences = computed<Array<EvidenceIntake>>(() => {
    return (this.displayedRequirement()?.evidences ?? []).filter((evidence) => evidence.mandatory);
  });

  protected readonly uploadedSlotIds = signal<Record<number, true>>({});
  protected readonly uploadedAttachmentsBySlotId = signal<
    Record<string, UploadedAttachmentViewModel>
  >({});

  protected readonly otherEvidence = signal<EvidenceIntake>(
    createInitialOtherEvidence(this.translateService),
  );

  constructor() {
    effect(() => {
      const requirementIntake = this.requirement();
      this.displayedRequirement.set(requirementIntake);
    });
    effect(() => {
      const displayedRequirement = this.displayedRequirement();
      this.otherEvidence.set(createInitialOtherEvidence(this.translateService));
      displayedRequirement?.evidences
        .filter((evidence) => !evidence.mandatory)
        .forEach((evidence) => {
          this.otherEvidence.set(evidence);
        });
    });

    effect(() => {
      const isCollapsed = this.collapsed();
      this.expanded.set(!isCollapsed);
    });
  }

  protected expand(): void {
    this.expanded.set(true);
  }

  protected readonly evidencesAddressed = computed<number>(() => {
    return (
      this.displayedRequirement()
        ?.evidences.map((evidence) => (evidence.fileAttachments.length ? 1 : 0))
        .reduce((acc: number, curr) => acc + curr, 0) ?? 0
    );
  });

  protected readonly mandatoryOrFilePresent = computed<number>(() => {
    return (
      this.displayedRequirement()
        ?.evidences.map((evidence) =>
          evidence.mandatory || evidence.fileAttachments.length ? 1 : 0,
        )
        .reduce((acc: number, curr) => acc + curr, 0) ?? 0
    );
  });

  protected isProvided(evidenceId: number): boolean {
    if (this.uploadedSlotIds()[evidenceId]) {
      return true;
    }

    const evidence = this.displayedRequirement()?.evidences.find((item) => item.id === evidenceId);
    return (evidence?.fileAttachments?.length ?? 0) > 0;
  }

  protected async onUploadSucceeded(event: {
    evidenceId: number;
    file: File;
    uploadResult: UploadEvidenceDocumentResult;
  }): Promise<void> {
    const { evidenceId, file, uploadResult } = event;
    this.replaceEvidence(evidenceId, {
      status: EvidenceStatus.Provided,
      fileAttachments: [
        ...(this.displayedRequirement()?.evidences.find((item) => item.id === evidenceId)
          ?.fileAttachments ?? []),
        uploadResult as FileAttachmentIntake,
      ],
    });
    this.uploadedSlotIds.update((current) => ({ ...current, [evidenceId]: true }));
    this.uploadedAttachmentsBySlotId.update((current) => ({
      ...current,
      [evidenceId]: {
        filename: uploadResult.filename ?? file.name,
        fileType: uploadResult.filetype ?? (file.type || 'FILE'),
      },
    }));
  }

  private replaceEvidence(evidenceId: number, patch: Partial<ProvidedEvidenceIntake>): void {
    this.displayedRequirement.update((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        evidences: current.evidences.map((evidence) =>
          evidence.id === evidenceId
            ? {
                ...evidence,
                ...patch,
              }
            : evidence,
        ),
      };
    });
    this.notifyReloadNeeded.emit(this.displayedRequirement()!.id);
  }

  protected onNotProvidedMarked(evidenceId: number): void {
    this.replaceEvidence(evidenceId, { status: EvidenceStatus.NotProvided });
  }

  onOtherEvidenceCreated(newOtherEvidence: ProvidedEvidenceIntake): void {
    this.otherEvidence.set(newOtherEvidence);
    this.displayedRequirement.update((current) => {
      if (!current) {
        return current;
      }

      const extendedEvidences = current.evidences.concat(newOtherEvidence);
      return {
        ...current,
        evidences: extendedEvidences,
      };
    });
  }
}
